// Generic container window (UI-3). One slot-grid component parameterised by a
// layout of SlotRefs + transaction hooks — the inventory (2×2 craft), crafting
// table (3×3), and creative palette (M4.4) are all just configs over this. All
// slot mutations run through the cursor state machine (transaction.clickSlot),
// so the UI never writes a slot directly (PLR-1). Client-only (ENG-1).
import { h, clear } from '../h';
import { type ItemStack, type Slots, emptyStack, isEmpty } from '../../../sim/game/inventory';
import { clickSlot, insert, type ClickWindow, type ClickButton } from '../../../sim/game/transaction';
import { CROPS, SEED_TO_CROP } from '../../../sim/game/farming';
import { heredityOn } from '../../../sim/heredity/rule';
import { cropTooltipLine } from '../../../sim/heredity/crops';
import type { Game } from '../../boot';

/** Title-cases an item/enchantment id for display (same convention used across
 *  the client's other screens — stats.ts/trade.ts/crafting.ts). */
function prettyName(id: string): string {
  return id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const ROMAN: readonly [number, string][] = [[10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
function roman(n: number): string {
  let out = '', v = n;
  for (const [val, sym] of ROMAN) while (v >= val) { out += sym; v -= val; }
  return out || String(n);
}

/** A window cell: a live slot in some container, optionally take-only/infinite. */
export interface SlotRef {
  slots: Slots;
  index: number;
  role?: 'output' | 'creative';
  /** Stable id rendered as `data-slot` (E2E click targets). */
  testId?: string;
  /** Hover tooltip (native `title`) — e.g. "Helmet", "Crafting Result". */
  title?: string;
}

export interface WindowConfig {
  title: string;
  /** Visual grid: rows of cells (SlotRef | null gap). */
  layout: (SlotRef | null)[][];
  /** Shift-click destination cells for `ref`. */
  quickTargets(ref: SlotRef): SlotRef[];
  /** Recompute derived slots (crafting output) after any change. */
  onChange?(): void;
  /** An output slot was taken — consume inputs (+ re-derive output). */
  onOutputTaken?(ref: SlotRef): void;
  /** Window is closing; `cursor` holds any un-placed items to return/drop. */
  onClose?(cursor: ItemStack): void;
  /** Handed a `repaint()` when the window opens — lets a live container (a
   *  cooking furnace) refresh its slots from the tick loop. */
  onOpen?(repaint: () => void): void;
  /** An extra element appended below the slot grid (M24 recipe book). */
  footer?: HTMLElement;
  /** A decorative element (e.g. the inventory screen's player profile doll)
   *  shown beside the first `headerRows` rows of `layout`, side-by-side rather
   *  than full-width-stacked. Ignored unless `headerRows` is also set. */
  header?: HTMLElement;
  /** How many leading `layout` rows render beside `header` in a flex row
   *  instead of the normal full-width stack (e.g. a vertical armor column next
   *  to a profile doll). Rows past this index render normally below. */
  headerRows?: number;
  /** `layout` row index → a section label rendered above that row (e.g.
   *  "Crafting", "Inventory"), matching Minecraft's labeled sections. */
  rowLabels?: Partial<Record<number, string>>;
}

/** Flattened Slots view over the window's unique SlotRefs (indices 0..n). */
class WindowSlots implements Slots {
  constructor(private readonly refs: SlotRef[]) {}
  get size(): number { return this.refs.length; }
  private ref(i: number): SlotRef { return this.refs[i]!; }
  get(i: number): ItemStack { const r = this.ref(i); return r.slots.get(r.index); }
  set(i: number, s: ItemStack): void { const r = this.ref(i); r.slots.set(r.index, s); }
  maxStackOf(item: number): number { return this.ref(0)?.slots.maxStackOf(item) ?? 64; }
  accepts(i: number, s: ItemStack): boolean {
    const r = this.ref(i);
    if (r.role) return false; // output/creative never accept deposits
    return r.slots.accepts(r.index, s);
  }
}

export function openWindow(game: Game, cfg: WindowConfig): () => void {
  // Unique refs → window index.
  const refs: SlotRef[] = [];
  const indexOf = new Map<SlotRef, number>();
  for (const row of cfg.layout) for (const cell of row) {
    if (cell && !indexOf.has(cell)) { indexOf.set(cell, refs.length); refs.push(cell); }
  }
  const winSlots = new WindowSlots(refs);
  const idxOf = (r: SlotRef): number => indexOf.get(r)!;

  const clickWin: ClickWindow = {
    slots: winSlots,
    quickMoveTargets: (from) => cfg.quickTargets(refs[from]!).map(idxOf),
    isOutput: (i) => refs[i]!.role === 'output',
    isCreativeSource: (i) => refs[i]!.role === 'creative',
    onTake: (i) => cfg.onOutputTaken?.(refs[i]!),
  };

  let cursor: ItemStack = emptyStack();

  game.uiOpen = true;
  document.exitPointerLock();

  const cursorEl = h('div', { class: 'bb-cursor' });
  const tooltipEl = h('div', { class: 'bb-tooltip' });
  tooltipEl.style.display = 'none';
  const overlay = h('div', { class: 'bb-window-overlay' });
  const panel = h('div', { class: 'bb-window' });
  overlay.append(panel, cursorEl, tooltipEl);
  document.body.append(overlay);

  const slotEls = new Map<SlotRef, HTMLElement>();

  // Hover tooltip (name + stats — attack damage, durability, food, enchants,
  // custom name/lore) for whichever slot the mouse is currently over. Rebuilt
  // from live state (not just on mouseenter) so it stays correct if the slot's
  // contents change underneath the cursor (a furnace ticking, a recipe click).
  let hovered: SlotRef | null = null;
  const updateTooltip = (): void => {
    const s = hovered ? hovered.slots.get(hovered.index) : null;
    if (!s || isEmpty(s)) { tooltipEl.style.display = 'none'; return; }
    clear(tooltipEl);
    const itemName = game.registries.items.nameOf[s.item];
    const def = itemName ? game.registries.items.byName.get(itemName) : undefined;
    const enchants = s.components?.enchants ?? [];
    const nameEl = h('div', { class: `bb-tooltip-name${enchants.length ? ' bb-tooltip-enchanted' : ''}` },
      s.components?.name ?? prettyName(itemName ?? '?'));
    tooltipEl.append(nameEl);
    for (const e of enchants) tooltipEl.append(h('div', { class: 'bb-tooltip-ench' }, `${prettyName(e.id)} ${roman(e.lvl)}`));
    if (def?.attackDamage != null) tooltipEl.append(h('div', { class: 'bb-tooltip-stat' }, `${def.attackDamage} Attack Damage`));
    if (def?.durability != null) {
      const used = s.components?.damage ?? 0;
      tooltipEl.append(h('div', { class: 'bb-tooltip-stat' }, `Durability: ${def.durability - used} / ${def.durability}`));
    }
    if (def?.food) tooltipEl.append(h('div', { class: 'bb-tooltip-stat' }, `Restores ${def.food.nutrition} Hunger`));
    // A genetic seed's second line (HER-8, ARCH §28.10, binding). `cropTooltipLine`
    // itself decides null vs a line — rule off, no genome, or no table ⇒ nothing.
    const cropLine = itemName
      ? cropTooltipLine(CROPS[SEED_TO_CROP[itemName] ?? '']?.genes, s.components?.genome, heredityOn(game.world))
      : null;
    if (cropLine) tooltipEl.append(h('div', { class: 'bb-tooltip-stat' }, cropLine));
    for (const line of s.components?.lore ?? []) tooltipEl.append(h('div', { class: 'bb-tooltip-lore' }, line));
    tooltipEl.style.display = 'block';
  };

  const paintSlot = (ref: SlotRef): void => {
    const el = slotEls.get(ref);
    if (!el) return;
    const s = ref.slots.get(ref.index);
    clear(el);
    if (!isEmpty(s)) {
      const url = game.icons.get(s.item);
      const icon = h('div', { class: 'bb-slot-icon', style: { backgroundImage: `url(${url})` } });
      el.append(icon);
      if (s.count > 1) el.append(h('span', { class: 'bb-slot-count' }, String(s.count)));
    }
  };
  const paintCursor = (): void => {
    clear(cursorEl);
    if (!isEmpty(cursor)) {
      cursorEl.append(h('div', { class: 'bb-slot-icon', style: { backgroundImage: `url(${game.icons.get(cursor.item)})` } }));
      if (cursor.count > 1) cursorEl.append(h('span', { class: 'bb-slot-count' }, String(cursor.count)));
    }
  };
  const repaintAll = (): void => { for (const ref of refs) paintSlot(ref); paintCursor(); updateTooltip(); };

  const onSlotDown = (ref: SlotRef, ev: MouseEvent): void => {
    ev.preventDefault();
    const button: ClickButton = ev.button === 2 ? 'right' : ev.button === 1 ? 'middle' : 'left';
    cursor = clickSlot(clickWin, cursor, { slot: idxOf(ref), button, shift: ev.shiftKey });
    cfg.onChange?.();
    repaintAll();
  };

  panel.append(h('div', { class: 'bb-window-title' }, cfg.title));

  const headRows = cfg.headerRows ?? 0;
  let rowsWrap: HTMLElement | null = null;
  if (cfg.header || headRows > 0) {
    const headGroup = h('div', { class: 'bb-window-head' });
    rowsWrap = h('div', { class: 'bb-window-head-rows' });
    headGroup.append(rowsWrap); // the head-rows (e.g. armor column) sit left…
    if (cfg.header) headGroup.append(cfg.header); // …the decorative header (doll) right
    panel.append(headGroup);
  }

  cfg.layout.forEach((row, i) => {
    const target = i < headRows && rowsWrap ? rowsWrap : panel;
    const label = cfg.rowLabels?.[i];
    if (label) target.append(h('div', { class: 'bb-row-label' }, label));
    const rowEl = h('div', { class: 'bb-slot-row' });
    for (const cell of row) {
      if (!cell) { rowEl.append(h('div', { class: 'bb-slot bb-slot-gap' })); continue; }
      const el = h('div', { class: `bb-slot${cell.role ? ' bb-slot-' + cell.role : ''}`, 'data-slot': cell.testId ?? '', title: cell.title });
      el.addEventListener('mousedown', (e) => onSlotDown(cell, e));
      el.addEventListener('contextmenu', (e) => e.preventDefault());
      el.addEventListener('mouseenter', () => { hovered = cell; updateTooltip(); });
      el.addEventListener('mouseleave', () => { hovered = null; tooltipEl.style.display = 'none'; });
      slotEls.set(cell, el);
      rowEl.append(el);
    }
    target.append(rowEl);
  });
  if (cfg.footer) panel.append(cfg.footer);

  const onMove = (e: MouseEvent): void => {
    cursorEl.style.left = `${e.clientX}px`;
    cursorEl.style.top = `${e.clientY}px`;
    tooltipEl.style.left = `${e.clientX + 16}px`;
    tooltipEl.style.top = `${e.clientY + 16}px`;
  };
  document.addEventListener('mousemove', onMove);

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('keydown', onKey, true);
    cfg.onClose?.(cursor);
    overlay.remove();
    game.uiOpen = false;
    if (game.settings) void game.renderer.domElement.requestPointerLock();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape' || e.code === 'KeyE') { e.preventDefault(); e.stopPropagation(); close(); }
  };
  document.addEventListener('keydown', onKey, true);

  // Clicking the dim backdrop (outside the panel) closes too.
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });

  cfg.onChange?.();
  repaintAll();
  cfg.onOpen?.(repaintAll);
  return close;
}

/** Build SlotRefs for a container over an index range (inclusive/exclusive).
 *  `idPrefix` sets each ref's `testId` to `${idPrefix}-${index}` (E2E targets). */
export function refRange(
  slots: Slots, start: number, end: number, role?: SlotRef['role'], idPrefix?: string, title?: string,
): SlotRef[] {
  const out: SlotRef[] = [];
  for (let i = start; i < end; i++) out.push({ slots, index: i, role, testId: idPrefix ? `${idPrefix}-${i}` : undefined, title });
  return out;
}

/** Return leftover items (the cursor / a cleared crafting grid) to the player.
 *  Item entities land in M4.3, so for now anything that doesn't fit is dropped
 *  on the ground via the host's drop hook. */
export function returnToInventory(game: Game, stacks: ItemStack[]): void {
  const inv = game.playerState.inventory;
  for (const s of stacks) {
    if (isEmpty(s)) continue;
    const rem = insert(inv, s, [...PlayerInventoryRange]);
    if (!isEmpty(rem)) game.dropItem?.(rem);
  }
}

/** Hotbar + storage indices (0..35) — the auto-collect range. */
const PlayerInventoryRange = Array.from({ length: 36 }, (_, i) => i);
